import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { useLinkPayCookies } from "../../common/hooks/useLinkPayCookies";
import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestGet from "../../components/apiHooks/useApiRequestGet";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { useUrlParams } from "../../router/hooks";
import { IOrderDetail } from "./types";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_ORDER_DETAIL ?? "";

const useGetOrderDetail = () => {
	const { merchantReference: paymentReference } = useUrlParams();
	const [orderDetail, setOrderDetail] = useState<IOrderDetail | null>(null);
	const {
		state: { config, user },
	} = useConfigContext((state) => state);
	// Passive: this hook only snapshots the token to pick an auth header. The active
	// default's reload-on-late-injection would reload the order-confirmation page right
	// after payment - the least reload-friendly moment in the app.
	const { accessToken } = useLinkPayCookies({ passive: true });

	const url = useMemo(() => {
		if (!paymentReference) return null;

		return `${HOST}${END_POINT}?uuid=${paymentReference}`;
	}, [paymentReference]);

	const payload = useMemo(() => {
		// Don't make request if URL is not available
		if (!url) {
			return {
				url: null,
				axiosConfig: undefined,
				swrConfig: {
					revalidateOnFocus: false,
				},
			};
		}

		const sendtoken = config?.send_linkpay_accesstoken ?? true; // default to true to send if flag does not exist

		// Determine which token to use: LinkPay access token takes precedence over user session
		const authToken = accessToken && sendtoken ? accessToken : (user?.session_id?.toString() ?? "");

		// Allow request even with empty token, but add retry logic for auth errors

		return {
			url,
			axiosConfig: addJWTInConfig(undefined, authToken),
			swrConfig: {
				revalidateOnFocus: false,
				shouldRetryOnError: (error: any) => {
					// Retry 401 errors (JWT might be initializing) but limit to 2 attempts
					if (error?.response?.status === 401) {
						return true;
					}

					// Only retry on 5xx server errors, not other 4xx client errors
					return error?.response?.status >= 500;
				},
				errorRetryCount: 2,
				errorRetryInterval: 1000, // 1 second delay between retries
			},
		};
	}, [url, accessToken, config, user]);

	const {
		data: response,
		isLoading,
		error,
		isValidating,
		getUpdateData,
	} = useApiRequestGet(payload);

	// Update orderDetail state when response changes
	useEffect(() => {
		const newOrderDetail = response
			? {
					...response,
					order_items: response?.order_items ?? null,
				}
			: null;

		if (!isEqual(orderDetail, newOrderDetail)) {
			setOrderDetail(newOrderDetail);
		}
	}, [response, orderDetail]);

	return {
		orderDetail,
		isLoading: isLoading || isValidating,
		error,
		refetch: getUpdateData,
	};
};

export default useGetOrderDetail;
